import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useBreadcrumbContext } from '../contexts/BreadcrumbContext';
import { useTenantInfo } from '../features/tenant/hooks/useUniversityAdmins';
import { useTenant } from '../features/manage/hooks/useTenants';
import { useDomain } from '../features/tenant/hooks/useDomains';
import { useDomainInfo } from '../features/domain/hooks/useSubjects';
import { useSubjectInfo } from '../features/subject/hooks/useSubjectUsers';

interface BreadcrumbManagerParams extends Record<string, string | undefined> {
  tenantId?: string;
  domainId?: string;
  subjectId?: string;
}

/**
 * Hook that automatically manages breadcrumb metadata by fetching entity names
 * when navigating to routes with IDs. Replaces IDs with human-readable names.
 */
export const useBreadcrumbManager = () => {
  const params = useParams<BreadcrumbManagerParams>();
  const { setMetadata, clearMetadata } = useBreadcrumbContext();

  // Extract IDs from route params
  const tenantId = params.tenantId;
  const domainId = params.domainId;
  const subjectId = params.subjectId;

  // Fetch tenant info when tenantId is available
  const { data: tenantInfo } = useTenantInfo(tenantId || '');
  const { data: tenantFromManage } = useTenant(tenantId || '');

  // Fetch domain info when domainId is available (requires tenantId for key lookup)
  const { data: domainInfo } = useDomain(tenantId || '', domainId || '');
  const { data: domainInfoFromDomain } = useDomainInfo(tenantId || '', domainId || '');

  // Fetch subject info when subjectId is available (requires tenantId for key lookup)
  const { data: subjectInfo } = useSubjectInfo(tenantId || '', subjectId || '');

  // Update breadcrumb metadata when data changes
  useEffect(() => {
    const newMetadata: Record<string, string | undefined> = {};

    // Set tenant information
    if (tenantId) {
      newMetadata.tenantId = tenantId;
      // Prefer tenant info from tenant API, fallback to manage API
      const tenantName = tenantInfo?.name || tenantFromManage?.name;
      const tenantCode = tenantInfo?.code || tenantFromManage?.code;
      newMetadata.tenantName = tenantName || undefined;
      newMetadata.tenantCode = tenantCode || undefined;
    }

    // Set domain information
    if (domainId) {
      newMetadata.domainId = domainId;
      // Prefer domain info from tenant API, fallback to domain API
      const domainName = domainInfo?.name || domainInfoFromDomain?.name;
      const domainCode = domainInfo?.code || domainInfoFromDomain?.code;
      newMetadata.domainName = domainName || undefined;
      newMetadata.domainCode = domainCode || undefined;
    }

    // Set subject information
    if (subjectId) {
      newMetadata.subjectId = subjectId;
      newMetadata.subjectName = subjectInfo?.name || undefined;
      newMetadata.subjectCode = subjectInfo?.code || undefined;
    }

    // Only update metadata if we have meaningful data
    if (Object.keys(newMetadata).length > 0) {
      setMetadata(newMetadata);
    }

    // Cleanup when component unmounts or params change
    return () => {
      if (!tenantId && !domainId && !subjectId) {
        clearMetadata();
      }
    };
  }, [
    tenantId,
    domainId,
    subjectId,
    tenantInfo?.name,
    tenantInfo?.code,
    tenantFromManage?.name,
    tenantFromManage?.code,
    domainInfo?.name,
    domainInfo?.code,
    domainInfoFromDomain?.name,
    domainInfoFromDomain?.code,
    subjectInfo?.name,
    subjectInfo?.code,
    setMetadata,
    clearMetadata,
  ]);

  return {
    tenantId,
    domainId,
    subjectId,
    tenantName: tenantInfo?.name || tenantFromManage?.name,
    tenantCode: tenantInfo?.code || tenantFromManage?.code,
    domainName: domainInfo?.name || domainInfoFromDomain?.name,
    domainCode: domainInfo?.code || domainInfoFromDomain?.code,
    subjectName: subjectInfo?.name,
    subjectCode: subjectInfo?.code,
    isLoadingTenant: !tenantInfo && !tenantFromManage && !!tenantId,
    isLoadingDomain: !domainInfo && !domainInfoFromDomain && !!domainId,
    isLoadingSubject: !subjectInfo && !!subjectId,
  };
};
